<template>
  <span class="cameraRecordings">
    <el-button
      icon="el-icon-download"
      type="text"
      @click="open"
    >Vídeos</el-button>
    <el-dialog
      append-to-body
      custom-class="cameraRecordingsDialog"
      :title="dialogTitle"
      :visible.sync="visible"
      width="90%"
      top="5vh"
      @closed="reset"
    >
      <div
        class="cr-status"
        :class="online ? 'is-online' : 'is-offline'"
      >
        <span class="cr-status__dot"></span>
        {{ online ? 'Câmera online' : 'Câmera offline' }}
        <span
          v-if="lastUpdateText"
          class="cr-status__time"
        >· visto {{ lastUpdateText }}</span>
      </div>

      <div class="cr-toolbar">
        <el-form inline>
          <el-form-item label="Câmera">
            <el-select v-model="channel" style="width:130px">
              <el-option label="Todas" :value="0"></el-option>
              <el-option
                v-for="cameraChannel in cameraChannels"
                :key="cameraChannel"
                :label="`Cam. ${cameraChannel}`"
                :value="cameraChannel"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="Evento">
            <el-select v-model="eventFilter" filterable style="width:210px">
              <el-option label="Todos" value="all"></el-option>
              <el-option label="Apenas com evento" value="any"></el-option>
              <el-option
                v-for="event in eventOptions"
                :key="event"
                :label="event"
                :value="event"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="Período">
            <el-date-picker
              v-model="range"
              type="datetimerange"
              range-separator="—"
              start-placeholder="Início"
              end-placeholder="Fim"
              :clearable="false"
              :picker-options="rangeShortcuts"
            ></el-date-picker>
          </el-form-item>
        </el-form>
        <div class="cr-toolbar__actions">
          <el-button
            type="primary"
            icon="el-icon-search"
            :loading="loading"
            @click="search"
          >Pesquisar</el-button>
          <el-button
            icon="el-icon-setting"
            :loading="configLoading"
            @click="checkConfiguration"
          >Verificar configuração</el-button>
        </div>
      </div>

      <div
        v-if="parameters || version || textRows.length"
        class="cr-config"
      >
        <div
          v-if="safetySummary"
          class="cr-config__summary"
          :class="'is-' + safetySummary.type"
        >
          <i :class="safetySummary.icon"></i>
          <span>{{ safetySummary.title }}</span>
        </div>
        <dl
          v-if="configRows.length"
          class="cr-config__grid"
        >
          <div
            v-for="row in configRows"
            :key="row.label"
            class="cr-config__item"
          >
            <dt>{{ row.label }}</dt>
            <dd>
              <span
                v-if="row.level === 'info'"
                class="cr-config__value"
              >{{ row.value }}</span>
              <el-tag
                v-else
                :type="row.level"
                size="mini"
                effect="plain"
              >{{ row.value }}</el-tag>
              <el-popconfirm
                v-if="enableTarget(row)"
                :title="`Ativar ${row.label} nesta câmera?`"
                confirm-button-text="Ativar"
                cancel-button-text="Cancelar"
                @confirm="enableParam(row)"
              >
                <el-button
                  slot="reference"
                  type="text"
                  size="mini"
                  class="cr-config__enable"
                  :loading="enablingParamId === row.paramId"
                >Ativar</el-button>
              </el-popconfirm>
            </dd>
          </div>
        </dl>
        <div
          v-if="textRows.length"
          class="cr-config__replies"
        >
          <div
            v-for="reply in textRows"
            :key="reply.label"
            class="cr-config__reply"
          >
            <div class="cr-config__reply-label">{{ reply.label }}</div>
            <div
              v-if="reply.kind === 'events'"
              class="cr-config__tags"
            >
              <template v-for="ev in reply.events">
                <el-popconfirm
                  v-if="ev.code"
                  :key="ev.key"
                  :title="`${ev.on ? 'Desativar' : 'Ativar'} ${ev.label} nesta câmera?`"
                  :confirm-button-text="ev.on ? 'Desativar' : 'Ativar'"
                  cancel-button-text="Cancelar"
                  @confirm="toggleDmsEvent(ev, !ev.on)"
                >
                  <el-tag
                    slot="reference"
                    size="mini"
                    effect="plain"
                    :type="ev.on ? 'success' : 'info'"
                    class="cr-config__tag-action"
                    :class="{ 'is-busy': enablingDmsEvent === ev.code }"
                  >{{ ev.label }}<template v-if="!ev.on"> · ativar</template></el-tag>
                </el-popconfirm>
                <el-tag
                  v-else
                  :key="ev.key"
                  size="mini"
                  effect="plain"
                  :type="ev.on ? 'success' : 'info'"
                >{{ ev.label }}<template v-if="!ev.on"> · off</template></el-tag>
              </template>
            </div>
            <div
              v-else-if="reply.kind === 'state'"
              class="cr-config__reply-state"
            >
              <el-tag
                size="mini"
                effect="plain"
                :type="reply.level"
              >{{ reply.state }}</el-tag>
              <code>{{ reply.detail }}</code>
              <el-popconfirm
                v-if="reply.jc181"
                :title="`${reply.on ? 'Desativar' : 'Ativar'} ${reply.label} nesta câmera?`"
                :confirm-button-text="reply.on ? 'Desativar' : 'Ativar'"
                cancel-button-text="Cancelar"
                @confirm="toggleJc181Event(reply, !reply.on)"
              >
                <el-button
                  slot="reference"
                  type="text"
                  size="mini"
                  class="cr-config__enable"
                  :loading="enablingJc181 === reply.jc181"
                >{{ reply.on ? 'Desativar' : 'Ativar' }}</el-button>
              </el-popconfirm>
            </div>
            <ul
              v-else-if="reply.kind === 'pairs'"
              class="cr-config__pairs"
            >
              <li
                v-for="pair in reply.pairs"
                :key="pair"
              >{{ pair }}</li>
            </ul>
            <code
              v-else
              class="cr-config__reply-code"
            >{{ reply.detail }}</code>
          </div>
        </div>
      </div>

      <el-alert
        v-if="message"
        :title="displayMessage"
        :type="messageType"
        :closable="false"
        show-icon
        style="margin-bottom:12px"
      ></el-alert>

      <div
        v-if="events.length"
        class="cr-events"
      >
        <div class="cr-events__title">
          Eventos ADAS/DMS ({{ events.length }})
        </div>
        <el-table
          :data="events"
          size="mini"
        >
          <el-table-column label="Evento" min-width="160">
            <template slot-scope="{ row }">
              <el-tag
                size="mini"
                effect="plain"
                :type="row.kind === 'dms' ? 'danger' : 'warning'"
              >{{ row.label }}</el-tag>
              <i
                v-if="row.hasVideo"
                class="el-icon-video-camera cr-events__level"
                title="clipe disponível"
              ></i>
              <span
                v-if="row.level"
                class="cr-events__level"
              >nível {{ row.level }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="Horário"
            min-width="150"
          >
            <template slot-scope="{ row }">{{ formatTime(row.time) }}</template>
          </el-table-column>
          <el-table-column
            label="Anexos"
            width="110"
          >
            <template slot-scope="{ row }">
              <el-button
                v-if="row.attachments && row.identifier"
                type="text"
                size="mini"
                :loading="attachmentsLoadingId === row.id"
                @click="openAttachments(row)"
              >Ver ({{ row.attachments }})</el-button>
              <span v-else>{{ row.attachments || '—' }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <el-table
        v-if="hasSearched"
        :data="filteredRecordings"
        empty-text="Nenhum vídeo encontrado"
      >
        <el-table-column label="Câmera" width="90">
          <template slot-scope="{ row }">Cam. {{ row.channel }}</template>
        </el-table-column>
        <el-table-column label="Início" min-width="165">
          <template slot-scope="{ row }">{{ formatTime(row.startTime) }}</template>
        </el-table-column>
        <el-table-column label="Fim" min-width="165">
          <template slot-scope="{ row }">{{ formatTime(row.endTime) }}</template>
        </el-table-column>
        <el-table-column label="Evento" min-width="150">
          <template slot-scope="{ row }">
            <template v-if="describeAlarms(row.alarmFlag).length">
              <el-tag
                v-for="label in describeAlarms(row.alarmFlag)"
                :key="label"
                size="mini"
                type="warning"
                style="margin:1px 2px"
              >{{ label }}</el-tag>
            </template>
            <span v-else>—</span>
          </template>
        </el-table-column>
        <el-table-column label="Tamanho" width="100">
          <template slot-scope="{ row }">{{ formatSize(row.fileSize) }}</template>
        </el-table-column>
        <el-table-column width="125" align="right">
          <template slot-scope="{ row }">
            <el-button
              size="mini"
              icon="el-icon-video-play"
              :loading="playing === recordingKey(row)"
              :disabled="Boolean(playing)"
              @click="play(row)"
            >Reproduzir</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-dialog
        append-to-body
        destroy-on-close
        custom-class="cameraRecordingsDialog"
        :visible.sync="playbackVisible"
        :title="playbackTitle || 'Reprodução'"
        width="70%"
        top="8vh"
        @closed="onPlaybackClosed"
      >
        <video
          ref="playbackVideo"
          controls
          autoplay
          playsinline
          style="display:block; width:100%; max-height:70vh; background:#000"
        ></video>
      </el-dialog>

      <el-dialog
        append-to-body
        custom-class="cameraRecordingsDialog"
        :visible.sync="attachmentDialogVisible"
        :title="attachmentEvent ? `Anexos · ${attachmentEvent.label} · ${formatTime(attachmentEvent.time)}` : 'Anexos'"
        width="70%"
        top="8vh"
      >
        <div
          v-loading="attachmentEvent && attachmentsLoadingId === attachmentEvent.id"
          class="cr-attachments"
        >
          <div
            v-for="file in attachmentFiles"
            :key="file.name"
            class="cr-attachments__item"
          >
            <video
              v-if="file.video"
              :src="file.url"
              controls
              playsinline
            ></video>
            <a
              v-else
              :href="file.url"
              target="_blank"
              rel="noopener"
            ><img
              :src="file.url"
              alt=""
            ></a>
            <span class="cr-attachments__name">{{ file.label }}</span>
          </div>
          <el-empty
            v-if="!attachmentFiles.length && (!attachmentEvent || attachmentsLoadingId !== attachmentEvent.id)"
            class="cr-attachments__empty"
            description="Nenhum anexo recebido para este evento."
          ></el-empty>
        </div>
      </el-dialog>
    </el-dialog>
  </span>
</template>

<script>
import moment from 'moment'
import {
  RECORDING_ALARM_EVENTS,
  SAFETY_PARAMETER_IDS,
  SAFETY_TEXT_QUERIES,
  describeParameters,
  describeRecordingAlarms,
  describeSafetyEvent,
  describeTextResult,
  enableParameterValue,
  getCameraChannels,
  getPlaybackWhepUrl,
  queryDeviceEvents,
  queryDeviceParameters,
  queryDeviceVersion,
  queryEventAttachments,
  queryRecordings,
  queryTextCommands,
  recordingAlarmQueryFlag,
  setDeviceParameter,
  setDmsEvent,
  setJc181Event,
  startPlayback
} from '@/utils/cameras'
import { serverBus } from '@/main'

const MAX_QUERY_RANGE = 31 * 24 * 60 * 60 * 1000
const CONFIG_REQUEST_MESSAGE = 'Solicitando a configuração da câmera…'
// Transport acks and command-rejection strings that Traccar also delivers as
// `result` - never a config readback, so keep them out of the panel.
const IGNORED_RESULTS = /^(0x8900 accepted|command error|Platform command is not matched|RTMP|OK\b)/i
// Plain scalar readbacks shown as their own "Parâmetros gerais" list, not in
// the safety-parameter grid.
const GENERAL_PARAM_LABELS = ['Intervalo de heartbeat (s)', 'Velocidade máxima (km/h)']

export default {
  name: 'CameraRecordings',
  props: {
    device: {
      type: Object,
      required: true
    }
  },
  data() {
    const end = new Date()
    const start = new Date(end.getTime() - 60 * 60 * 1000)
    return {
      visible: false,
      channel: 0,
      range: [start, end],
      recordings: [],
      events: [],
      eventsLoading: false,
      attachmentsByEvent: {},
      attachmentsLoadingId: '',
      attachmentEvent: null,
      attachmentDialogVisible: false,
      eventFilter: 'all',
      loading: false,
      playing: '',
      playbackVisible: false,
      playbackTitle: '',
      playbackPeerConnection: null,
      playbackStream: null,
      message: '',
      messageType: 'info',
      queryStartedAt: 0,
      responseResolve: null,
      responseReject: null,
      responseTimer: null,
      hasSearched: false,
      parameters: null,
      parametersRaw: null,
      version: null,
      textResults: [],
      configLoading: false,
      enablingParamId: 0,
      enablingDmsEvent: 0,
      enablingJc181: '',
      awaitingRequery: '',
      configRequestedAt: 0,
      configTimer: null
    }
  },
  created() {
    serverBus.$on('cameraVideoResources', this.handleVideoResources)
    serverBus.$on('cameraParameters', this.handleParameters)
    serverBus.$on('cameraVersion', this.handleVersion)
    serverBus.$on('cameraResult', this.handleResult)
    serverBus.$on('cameraSafetyEvent', this.handleSafetyEvent)
  },
  beforeDestroy() {
    serverBus.$off('cameraVideoResources', this.handleVideoResources)
    serverBus.$off('cameraParameters', this.handleParameters)
    serverBus.$off('cameraVersion', this.handleVersion)
    serverBus.$off('cameraResult', this.handleResult)
    serverBus.$off('cameraSafetyEvent', this.handleSafetyEvent)
    if (this.configTimer) clearTimeout(this.configTimer)
    this.stopPlayback()
  },
  computed: {
    rangeShortcuts() {
      const back = ms => picker => {
        const end = new Date()
        picker.$emit('pick', [new Date(end.getTime() - ms), end])
      }
      const hour = 60 * 60 * 1000
      return {
        shortcuts: [
          { text: 'Última hora', onClick: back(hour) },
          { text: 'Últimas 6 h', onClick: back(6 * hour) },
          {
            text: 'Hoje',
            onClick(picker) {
              const end = new Date()
              const start = new Date(end)
              start.setHours(0, 0, 0, 0)
              picker.$emit('pick', [start, end])
            }
          },
          { text: 'Últimas 24 h', onClick: back(24 * hour) },
          { text: 'Últimos 7 dias', onClick: back(7 * 24 * hour) },
          { text: 'Últimos 30 dias', onClick: back(30 * 24 * hour) }
        ]
      }
    },
    liveDevice() {
      const fromStore = this.$store && this.$store.getters.deviceById
        ? this.$store.getters.deviceById(this.device.id)
        : null
      return fromStore || this.device
    },
    dialogTitle() {
      const name = this.liveDevice.name || this.device.name
      return name ? `Vídeos da câmera · ${name}` : 'Vídeos da câmera'
    },
    online() {
      return this.liveDevice.status === 'online'
    },
    lastUpdateText() {
      const value = this.liveDevice.lastUpdate
      return value ? moment(value).format('DD/MM HH:mm') : ''
    },
    cameraChannels() {
      return getCameraChannels(this.device).map(channel => channel.index)
    },
    eventOptions() {
      const events = new Set(RECORDING_ALARM_EVENTS.map(event => event.label))
      this.recordings.forEach(recording => {
        describeRecordingAlarms(recording.alarmFlag).forEach(label => events.add(label))
      })
      return Array.from(events).sort((first, second) => first.localeCompare(second))
    },
    filteredRecordings() {
      if (this.eventFilter === 'all') return this.recordings
      return this.recordings.filter(recording => {
        const labels = describeRecordingAlarms(recording.alarmFlag)
        return this.eventFilter === 'any' ? labels.length > 0 : labels.includes(this.eventFilter)
      })
    },
    displayMessage() {
      if (!this.message || this.eventFilter === 'all' || !this.recordings.length) return this.message
      return `${this.message} Mostrando ${this.filteredRecordings.length} com o filtro atual.`
    },
    configSummary() {
      switch (this.parameters && this.parameters.activeSafety) {
        case 'configured':
          return {
            type: 'success',
            icon: 'el-icon-circle-check',
            title: 'Detecção de segurança ativa (ADAS/DSM) configurada nesta câmera.'
          }
        case 'present-but-disabled':
          return {
            type: 'warning',
            icon: 'el-icon-warning-outline',
            title: 'ADAS/DSM disponível, mas nenhum parâmetro está ativado — a câmera não gera eventos.'
          }
        case 'not-supported':
          return {
            type: 'error',
            icon: 'el-icon-circle-close',
            title: 'Esta câmera não expõe parâmetros ADAS/DSM — segurança ativa não suportada/provisionada.'
          }
        default:
          return {
            type: 'info',
            icon: 'el-icon-info',
            title: 'Configuração recebida.'
          }
      }
    },
    // Verdict shown at the top of the config card. When the camera answered a
    // text query (JC450/JC371/JC181), derive it from those events - the JT/T 808
    // "not-supported" line is misleading once we have the real readback.
    safetySummary() {
      let on = 0
      let total = 0
      this.textRows.forEach(reply => {
        if (reply.kind === 'events') {
          reply.events.forEach(event => {
            total += 1
            if (event.on) on += 1
          })
        } else if (reply.kind === 'state') {
          total += 1
          if (reply.level === 'success') on += 1
        }
      })
      if (total) {
        if (on === 0) {
          return {
            type: 'warning',
            icon: 'el-icon-warning-outline',
            title: `Nenhum dos ${total} eventos de segurança está ativo nesta câmera.`
          }
        }
        return {
          type: on === total ? 'success' : 'info',
          icon: on === total ? 'el-icon-circle-check' : 'el-icon-info',
          title: `${on} de ${total} eventos de segurança ativos nesta câmera.`
        }
      }
      return this.parameters ? this.configSummary : null
    },
    configRows() {
      const rows = []
      if (this.version && this.version.model) {
        rows.push({ label: 'Modelo', value: this.version.model, level: 'info' })
      }
      if (this.parameters && this.parameters.rows) {
        rows.push(...this.parameters.rows.filter(row => !GENERAL_PARAM_LABELS.includes(row.label)))
      }
      return rows
    },
    // Firmware/hardware from the JT/T 808 version query are folded into the
    // "Versão dos módulos" text reply so every build string sits in one list.
    versionPairs() {
      const pairs = []
      if (this.version && this.version.fw) pairs.push(`Firmware: ${this.version.fw}`)
      if (this.version && this.version.hw) pairs.push(`Hardware: ${this.version.hw}`)
      return pairs
    },
    generalPairs() {
      if (!this.parameters || !this.parameters.rows) return []
      return this.parameters.rows
        .filter(row => GENERAL_PARAM_LABELS.includes(row.label))
        .map(row => `${row.label}: ${row.value}`)
    },
    textRows() {
      const rows = this.textResults
        .map(result => describeTextResult(result.raw))
        .filter(Boolean)
      if (this.generalPairs.length) {
        rows.push({ kind: 'pairs', label: 'Parâmetros gerais', pairs: this.generalPairs })
      }
      const extra = this.versionPairs
      if (!extra.length) return rows
      const modules = rows.find(row => row.label === 'Versão dos módulos' && row.kind === 'pairs')
      if (modules) {
        modules.pairs = [...extra, ...modules.pairs.filter(pair => !extra.includes(pair))]
        return rows
      }
      return [...rows, { kind: 'pairs', label: 'Versão dos módulos', pairs: extra }]
    },
    attachmentFiles() {
      return (this.attachmentEvent && this.attachmentsByEvent[this.attachmentEvent.id]) || []
    }
  },
  methods: {
    handleVideoResources(position) {
      if (!this.visible || !this.queryStartedAt || !position ||
        position.deviceId !== this.device.id || !position.attributes ||
        !Object.prototype.hasOwnProperty.call(position.attributes, 'videoResources')) return
      const value = position.attributes.videoResources
      try {
        const recordings = this.parseVideoResources(value)
        if (!Array.isArray(recordings)) {
          throw new Error('Invalid video resource list')
        }
        if (this.responseResolve) {
          const resolve = this.responseResolve
          this.clearResponseWaiter()
          resolve(recordings)
          return
        }
        // Not awaiting a search batch. Starting a playback makes the camera emit
        // its own videoResources reply - don't let that wipe a list we already
        // have on screen.
        if (!this.loading && this.recordings.length) {
          return
        }
        this.recordings = recordings
        this.message = this.recordings.length
          ? `${this.recordings.length} vídeo(s) encontrado(s).`
          : 'Nenhum vídeo encontrado no período.'
        this.messageType = this.recordings.length ? 'success' : 'info'
        this.loading = false
      } catch (error) {
        if (this.responseReject) {
          const reject = this.responseReject
          this.clearResponseWaiter()
          reject(error)
          return
        }
        this.message = 'A câmera retornou uma lista inválida.'
        this.messageType = 'error'
        this.loading = false
      }
    },
    parseVideoResources(value) {
      if (typeof value !== 'string') return value
      let json = value
      const decoder = document.createElement('textarea')
      for (let index = 0; index < 3; index++) {
        decoder.innerHTML = json
        const decoded = decoder.value
        if (decoded === json) break
        json = decoded
      }
      return JSON.parse(json)
    },
    handleParameters(position) {
      if (!this.visible || !this.configRequestedAt || !position ||
        position.deviceId !== this.device.id || !position.attributes ||
        !Object.prototype.hasOwnProperty.call(position.attributes, 'parameters')) return
      if (this.configTimer) {
        clearTimeout(this.configTimer)
        this.configTimer = null
      }
      // The 0x8104 (all) and 0x8106 (specific) probes each arrive as their own
      // 0x0104 reply; merge their raw maps so later replies augment, not replace.
      const parsed = describeParameters(position.attributes.parameters)
      this.parametersRaw = { ...(this.parametersRaw || {}), ...parsed.raw }
      this.parameters = describeParameters(this.parametersRaw)
      this.configLoading = false
      if (this.message === CONFIG_REQUEST_MESSAGE) {
        this.message = ''
      }
    },
    handleVersion(position) {
      if (!this.visible || !this.configRequestedAt || !position ||
        position.deviceId !== this.device.id || !position.attributes) return
      const attributes = position.attributes
      const hasVersion = ['terminalModel', 'versionHw', 'versionFw']
        .some(key => Object.prototype.hasOwnProperty.call(attributes, key))
      if (!hasVersion) return
      this.version = {
        model: attributes.terminalModel || '',
        hw: attributes.versionHw || '',
        fw: attributes.versionFw || ''
      }
    },
    handleResult(position) {
      if (!this.visible || !this.configRequestedAt || !position ||
        position.deviceId !== this.device.id || !position.attributes ||
        !Object.prototype.hasOwnProperty.call(position.attributes, 'result')) return
      const text = String(position.attributes.result || '').trim()
      if (!text || IGNORED_RESULTS.test(text)) return
      const parsed = describeTextResult(text)
      if (!parsed) return
      if (this.configTimer) {
        clearTimeout(this.configTimer)
        this.configTimer = null
      }
      const existing = this.textResults.find(row => row.label === parsed.label)
      if (existing) {
        existing.raw = text
      } else {
        this.textResults.push({ label: parsed.label, raw: text })
      }
      this.configLoading = false
      if (this.message === CONFIG_REQUEST_MESSAGE
        || (this.awaitingRequery && parsed.label === this.awaitingRequery)) {
        this.message = ''
        this.awaitingRequery = ''
      }
    },
    async checkConfiguration() {
      this.parameters = null
      this.parametersRaw = null
      this.version = null
      this.textResults = []
      this.configLoading = true
      this.configRequestedAt = Date.now()
      this.message = CONFIG_REQUEST_MESSAGE
      this.messageType = 'info'
      if (this.configTimer) clearTimeout(this.configTimer)
      this.configTimer = setTimeout(() => {
        this.configTimer = null
        this.configLoading = false
        this.message = 'A câmera não respondeu à consulta de configuração.'
        this.messageType = 'error'
      }, 30000)
      try {
        const deviceType = Number(this.liveDevice.attributes && this.liveDevice.attributes.deviceType)
        const tasks = [
          queryDeviceParameters(this.device.id),
          queryDeviceParameters(this.device.id, SAFETY_PARAMETER_IDS),
          queryDeviceVersion(this.device.id)
        ]
        // The Jimi cameras have no usable ADAS/DSM parameter block; read the
        // safety config back over the Jimi text protocol instead (replies arrive
        // as position "result"). Command set is per model.
        const textQueries = SAFETY_TEXT_QUERIES[deviceType]
        if (textQueries) {
          tasks.push(queryTextCommands(this.device.id, textQueries))
        }
        await Promise.all(tasks)
      } catch (error) {
        clearTimeout(this.configTimer)
        this.configTimer = null
        this.configLoading = false
        this.message = error.message || 'Não foi possível consultar a configuração.'
        this.messageType = 'error'
      }
    },
    enableTarget(row) {
      if (!row || !row.paramId) return null
      return enableParameterValue(row.paramId, row.current)
    },
    async enableParam(row) {
      const value = this.enableTarget(row)
      if (!value || this.enablingParamId) return
      this.enablingParamId = row.paramId
      this.message = `Enviando comando para ${row.label}…`
      this.messageType = 'info'
      try {
        await setDeviceParameter(this.device.id, row.paramId, value)
        this.message = `Comando enviado para ${row.label}. Reconsulte a configuração para confirmar.`
        this.messageType = 'success'
      } catch (error) {
        this.message = error.message || 'Falha ao enviar o comando.'
        this.messageType = 'error'
      } finally {
        this.enablingParamId = 0
      }
    },
    async toggleDmsEvent(ev, on) {
      if (!ev || !ev.code || this.enablingDmsEvent) return
      this.enablingDmsEvent = ev.code
      this.message = `${on ? 'Ativando' : 'Desativando'} ${ev.label}…`
      this.messageType = 'info'
      try {
        await setDmsEvent(this.device.id, ev.code, on)
        this.message = `Comando enviado para ${ev.label}. Reconsultando…`
        this.messageType = 'success'
        this.awaitingRequery = 'Eventos DMS'
        setTimeout(() => queryTextCommands(this.device.id, ['DMSSEP#']), 4000)
      } catch (error) {
        this.message = error.message || 'Falha ao enviar o comando.'
        this.messageType = 'error'
      } finally {
        this.enablingDmsEvent = 0
      }
    },
    async toggleJc181Event(reply, on) {
      if (!reply || !reply.jc181 || this.enablingJc181) return
      this.enablingJc181 = reply.jc181
      this.message = `${on ? 'Ativando' : 'Desativando'} ${reply.label}…`
      this.messageType = 'info'
      try {
        await setJc181Event(this.device.id, reply.jc181, reply.fields, on)
        this.message = `Comando enviado para ${reply.label}. Reconsultando…`
        this.messageType = 'success'
        this.awaitingRequery = reply.label
        setTimeout(() => queryTextCommands(this.device.id, [`${reply.jc181}#`]), 4000)
      } catch (error) {
        this.message = error.message || 'Falha ao enviar o comando.'
        this.messageType = 'error'
      } finally {
        this.enablingJc181 = ''
      }
    },
    open() {
      this.visible = true
    },
    reset() {
      this.loading = false
      this.message = ''
      this.eventFilter = 'all'
      this.hasSearched = false
      this.parameters = null
      this.parametersRaw = null
      this.version = null
      this.configLoading = false
      this.enablingParamId = 0
      this.configRequestedAt = 0
      if (this.configTimer) {
        clearTimeout(this.configTimer)
        this.configTimer = null
      }
      this.clearResponseWaiter()
      this.stopPlayback()
    },
    clearResponseWaiter() {
      if (this.responseTimer) {
        clearTimeout(this.responseTimer)
      }
      this.responseResolve = null
      this.responseReject = null
      this.responseTimer = null
    },
    queryRecordingBatch(start, end, alarmFlag) {
      return new Promise((resolve, reject) => {
        this.responseResolve = resolve
        this.responseReject = reject
        this.responseTimer = setTimeout(() => {
          this.clearResponseWaiter()
          reject(new Error('A câmera não respondeu à consulta.'))
        }, 30000)
        queryRecordings(
          this.device.id,
          this.channel,
          start.toISOString(),
          end.toISOString(),
          alarmFlag
        ).catch(error => {
          this.clearResponseWaiter()
          reject(error)
        })
      })
    },
    async search() {
      this.clearResponseWaiter()
      this.hasSearched = true
      this.recordings = []
      this.events = []
      this.attachmentsByEvent = {}
      this.loadEvents(this.range[0], this.range[1])
      const alarmFlag = recordingAlarmQueryFlag(this.eventFilter)
      const rangeDuration = this.range[1].getTime() - this.range[0].getTime()
      if (rangeDuration <= 0 || rangeDuration > MAX_QUERY_RANGE) {
        this.message = 'Selecione um período de até um mês.'
        this.messageType = 'error'
        this.loading = false
        return
      }
      this.message = 'Consultando os vídeos armazenados na câmera…'
      this.messageType = 'info'
      this.loading = true
      this.queryStartedAt = Date.now()
      try {
        const start = this.range[0]
        const end = this.range[1]
        const day = 24 * 60 * 60 * 1000
        const batchCount = Math.ceil((end.getTime() - start.getTime()) / day)
        const recordings = new Map()
        const invalidRecordings = new Set()
        for (let index = 0; index < batchCount; index++) {
          const batchStart = new Date(start.getTime() + index * day)
          const batchEnd = new Date(Math.min(batchStart.getTime() + day, end.getTime()))
          this.message = batchCount > 1
            ? `Consultando vídeos… ${index + 1} de ${batchCount}`
            : 'Consultando os vídeos armazenados na câmera…'
          const batch = await this.queryRecordingBatch(batchStart, batchEnd, alarmFlag)
          batch.forEach(recording => {
            const key = this.recordingKey(recording)
            if (this.isRecordingInRange(recording, start, end)) {
              recordings.set(key, recording)
            } else {
              invalidRecordings.add(key)
            }
          })
          this.recordings = Array.from(recordings.values())
            .sort((first, second) => Number(first.startTime) - Number(second.startTime))
        }
        this.message = this.recordings.length
          ? `${this.recordings.length} vídeo(s) encontrado(s).${invalidRecordings.size
            ? ` ${invalidRecordings.size} registro(s) com data inválida ignorado(s).`
            : ''}`
          : 'Nenhum vídeo encontrado no período.'
        this.messageType = invalidRecordings.size ? 'warning' : (this.recordings.length ? 'success' : 'info')
      } catch (error) {
        this.message = this.recordings.length
          ? `${this.recordings.length} vídeo(s) encontrado(s). A consulta não foi concluída.`
          : error.message || 'Não foi possível consultar a câmera.'
        this.messageType = this.recordings.length ? 'warning' : 'error'
      } finally {
        this.clearResponseWaiter()
        this.loading = false
      }
    },
    describeAlarms(alarmFlag) {
      return describeRecordingAlarms(alarmFlag)
    },
    async loadEvents(from, to) {
      this.eventsLoading = true
      try {
        const imei = (this.liveDevice && this.liveDevice.uniqueId) || this.device.uniqueId
        this.events = await queryDeviceEvents(this.device.id, from, to, imei)
      } catch (error) {
        this.events = []
      } finally {
        this.eventsLoading = false
      }
    },
    handleSafetyEvent(position) {
      if (!this.visible || !position || position.deviceId !== this.device.id) return
      const event = describeSafetyEvent(position)
      if (!event || this.events.some(existing => existing.id === event.id)) return
      this.events = [event, ...this.events].sort((first, second) => second.time - first.time)
    },
    openAttachments(event) {
      this.attachmentEvent = event
      this.attachmentDialogVisible = true
      if (!this.attachmentsByEvent[event.id]) {
        this.loadAttachments(event)
      }
    },
    async loadAttachments(event) {
      const imei = (this.liveDevice && this.liveDevice.uniqueId) || this.device.uniqueId
      if (!imei || !event.identifier) return
      this.attachmentsLoadingId = event.id
      try {
        const files = await queryEventAttachments(imei, event.identifier)
        this.$set(this.attachmentsByEvent, event.id, files)
      } catch (error) {
        this.$set(this.attachmentsByEvent, event.id, [])
      } finally {
        this.attachmentsLoadingId = ''
      }
    },
    recordingKey(recording) {
      return `${recording.channel}-${recording.startTime}-${recording.endTime}`
    },
    isRecordingInRange(recording, rangeStart, rangeEnd) {
      const startTime = Number(recording.startTime)
      const endTime = Number(recording.endTime)
      return Number.isFinite(startTime) &&
        Number.isFinite(endTime) &&
        startTime <= endTime &&
        startTime >= rangeStart.getTime() &&
        endTime <= rangeEnd.getTime()
    },
    async play(recording) {
      const key = this.recordingKey(recording)
      this.stopPlayback()
      this.playing = key
      this.message = 'Preparando a reprodução…'
      this.messageType = 'info'
      try {
        await startPlayback(this.device.id, recording)

        const peerConnection = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        })
        this.playbackPeerConnection = peerConnection
        const streamPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('A câmera não iniciou a reprodução.')), 30000)
          peerConnection.ontrack = event => {
            clearTimeout(timeout)
            resolve(event.streams[0])
          }
        })
        peerConnection.addTransceiver('video', { direction: 'recvonly' })
        peerConnection.addTransceiver('audio', { direction: 'recvonly' })
        const offer = await peerConnection.createOffer()
        await peerConnection.setLocalDescription(offer)
        let response
        for (let attempt = 0; attempt < 10; attempt++) {
          response = await fetch(getPlaybackWhepUrl(this.device.uniqueId, recording.channel - 1), {
            method: 'POST',
            headers: { 'Content-Type': 'application/sdp' },
            body: offer.sdp
          })
          if (response.ok) break
          await new Promise(resolve => setTimeout(resolve, 2000))
        }
        if (!response || !response.ok) {
          throw new Error(`Falha ao abrir o vídeo (${response ? response.status : 'sem resposta'}).`)
        }
        await peerConnection.setRemoteDescription({
          type: 'answer',
          sdp: await response.text()
        })

        const stream = await streamPromise
        this.playbackStream = stream
        this.playbackTitle = `Cam. ${recording.channel} · ${this.formatTime(recording.startTime)}`
        this.playbackVisible = true
        await this.$nextTick()
        // el-dialog mounts its body a tick after `visible` flips; wait for the
        // <video> ref before attaching the stream.
        for (let attempt = 0; attempt < 10 && !this.$refs.playbackVideo; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 50))
        }
        if (this.$refs.playbackVideo) {
          this.$refs.playbackVideo.srcObject = stream
        }
        this.message = 'Reprodução iniciada.'
        this.messageType = 'success'
      } catch (error) {
        this.stopPlayback()
        this.message = error.message || 'Não foi possível reproduzir o vídeo.'
        this.messageType = 'error'
      } finally {
        this.playing = ''
      }
    },
    onPlaybackClosed() {
      // Ignore the close that play() itself triggers when restarting playback;
      // only tear down on a real user close (X / Esc / overlay).
      if (!this.playing) {
        this.stopPlayback()
      }
    },
    stopPlayback() {
      const video = this.$refs.playbackVideo
      if (video && video.srcObject) {
        video.srcObject = null
      }
      if (this.playbackStream) {
        this.playbackStream.getTracks().forEach(track => track.stop())
        this.playbackStream = null
      }
      if (this.playbackPeerConnection) {
        this.playbackPeerConnection.close()
        this.playbackPeerConnection = null
      }
      this.playing = ''
      this.playbackVisible = false
      this.playbackTitle = ''
    },
    formatTime(value) {
      return moment(Number(value)).format('DD/MM/YYYY HH:mm:ss')
    },
    formatSize(value) {
      if (!value) return '—'
      const units = ['B', 'KB', 'MB', 'GB']
      const unit = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
      return `${(value / Math.pow(1024, unit)).toFixed(unit ? 1 : 0)} ${units[unit]}`
    }
  }
}
</script>

<style>
.cameraRecordingsDialog {
  max-width: 1200px;
  border-radius: 10px;
  overflow: hidden;
}

.cameraRecordingsDialog .el-dialog__header {
  padding: 18px 24px 14px;
  border-bottom: 1px solid #f0f0f0;
}

.cameraRecordingsDialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
}

.cameraRecordingsDialog .el-dialog__body {
  max-height: 78vh;
  overflow-y: auto;
  padding: 20px 24px 24px;
}

.cameraRecordingsDialog .cr-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #606266;
  margin-bottom: 14px;
}

.cameraRecordingsDialog .cr-status__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cameraRecordingsDialog .cr-status.is-online .cr-status__dot {
  background: #67c23a;
  box-shadow: 0 0 0 3px rgba(103, 194, 58, 0.18);
}

.cameraRecordingsDialog .cr-status.is-offline .cr-status__dot {
  background: #c0c4cc;
}

.cameraRecordingsDialog .cr-status__time {
  color: #909399;
}

.cameraRecordingsDialog .cr-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 4px 20px;
}

.cameraRecordingsDialog .cr-toolbar .el-form--inline .el-form-item {
  margin-right: 20px;
  margin-bottom: 14px;
}

.cameraRecordingsDialog .cr-toolbar__actions {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.cameraRecordingsDialog .cr-config {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
  margin: 4px 0 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.cameraRecordingsDialog .cr-config__summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
}

.cameraRecordingsDialog .cr-config__summary i {
  font-size: 17px;
  flex-shrink: 0;
}

.cameraRecordingsDialog .cr-config__summary.is-success {
  background: #f0f9eb;
  color: #529b2e;
}

.cameraRecordingsDialog .cr-config__summary.is-warning {
  background: #fdf6ec;
  color: #b88230;
}

.cameraRecordingsDialog .cr-config__summary.is-error {
  background: #fef0f0;
  color: #c45656;
}

.cameraRecordingsDialog .cr-config__summary.is-info {
  background: #f4f4f5;
  color: #73767a;
}

.cameraRecordingsDialog .cr-config__grid {
  margin: 0;
  padding: 4px 0;
  background: #fff;
}

.cameraRecordingsDialog .cr-config__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 16px;
}

.cameraRecordingsDialog .cr-config__item + .cr-config__item {
  border-top: 1px solid #f5f5f5;
}

.cameraRecordingsDialog .cr-config__item dt {
  color: #606266;
  font-size: 13px;
  flex-shrink: 0;
}

.cameraRecordingsDialog .cr-config__item dd {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 0;
}

.cameraRecordingsDialog .cr-config__value {
  font-size: 13px;
  text-align: right;
  word-break: break-word;
}

.cameraRecordingsDialog .cr-config__enable {
  padding: 0;
}

.cameraRecordingsDialog .cr-events {
  margin-bottom: 12px;
}

.cameraRecordingsDialog .cr-events__title {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 6px;
}

.cameraRecordingsDialog .cr-events__level {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}

.cameraRecordingsDialog .cr-attachments {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  min-height: 80px;
}

@media (max-width: 620px) {
  .cameraRecordingsDialog .cr-attachments {
    grid-template-columns: 1fr;
  }
}

.cameraRecordingsDialog .cr-attachments__item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.cameraRecordingsDialog .cr-attachments__empty {
  grid-column: 1 / -1;
}

.cameraRecordingsDialog .cr-attachments__item > a {
  display: block;
  line-height: 0;
}

.cameraRecordingsDialog .cr-attachments__item img,
.cameraRecordingsDialog .cr-attachments__item video {
  display: block;
  width: 100%;
  height: 220px;
  object-fit: cover;
  border-radius: 4px;
  background: #000;
}

.cameraRecordingsDialog .cr-attachments__item video {
  object-fit: contain;
}

.cameraRecordingsDialog .cr-attachments__name {
  font-size: 11px;
  color: #909399;
  text-align: center;
}

.cameraRecordingsDialog .cr-config__replies {
  background: #fff;
  border-top: 1px solid #ebeef5;
  padding: 4px 0;
}

.cameraRecordingsDialog .cr-config__reply {
  padding: 10px 16px;
}

.cameraRecordingsDialog .cr-config__reply + .cr-config__reply {
  border-top: 1px solid #f5f5f5;
}

.cameraRecordingsDialog .cr-config__reply-label {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 6px;
}

.cameraRecordingsDialog .cr-config__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.cameraRecordingsDialog .cr-config__tag-action {
  cursor: pointer;
  border-style: dashed;
}

.cameraRecordingsDialog .cr-config__tag-action:hover {
  color: #409eff;
  border-color: #409eff;
}

.cameraRecordingsDialog .cr-config__tag-action.is-busy {
  opacity: 0.5;
  pointer-events: none;
}

.cameraRecordingsDialog .cr-config__reply-state {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cameraRecordingsDialog .cr-config__reply-state code,
.cameraRecordingsDialog .cr-config__reply-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  color: #909399;
  word-break: break-all;
}

.cameraRecordingsDialog .cr-config__reply-code {
  display: block;
}

.cameraRecordingsDialog .cr-config__pairs {
  margin: 0;
  padding: 0;
  list-style: none;
  /* as many ~240px columns as fit - 1 for long version strings, 2-3 for
     short key:value pairs */
  column-width: 240px;
  column-gap: 16px;
}

.cameraRecordingsDialog .cr-config__pairs li {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  color: #606266;
  break-inside: avoid;
  padding: 1px 0;
  word-break: break-word;
}
</style>
